import { runInAction, computed } from 'mobx';
import GdsApi from '~/glm/utils/gds-api';
import Collection from '~/glm/models/collection';
import GdsHouseholdCollection from '~/glm/models/gds/gds-household-collection';
import EventCollection from '~/glm/models/gds/event-collection';
import GdsHouseholdModel from '~/glm/models/gds/gds-household-model';
import GdsEventModel from '~/glm/models/gds/gds-event-model';
import GuestWedding from '~/glm/models/gds/guest-wedding-model';
import GdsGuestModel from '~/glm/models/gds/gds-guest-model';
import GdsInvitationModel from './gds/gds-invitation-model';
import GuestCollection from './gds/gds-guest-collection';

export default class GuestListStore {
  static householdsEndpoint = '/v1/households';
  static apiEndpoint = '/v2/member_weddings';
  invitations;
  guests;
  events;
  guestWedding;

  constructor() {
    this.invitations = new Collection();
    this.guests = new GuestCollection();
    this.households = new GdsHouseholdCollection();
    this.gdsEvents = new EventCollection();
    this.guestWedding = new GuestWedding();

    GdsInvitationModel.prototype.collection = this.invitations;
    GdsGuestModel.prototype.collection = this.guests;
    GdsGuestModel.prototype.invitationCollection = this.invitations;
    GdsEventModel.prototype.collection = this.gdsEvents;
    GdsEventModel.prototype.invitationCollection = this.invitations;
    GdsHouseholdModel.prototype.guestCollection = this.guests;
    GdsEventModel.prototype.householdCollection = this.households;
  }

  fetchCollections() {
    this.clearStore();
    return GdsApi.get('/v1/member-weddings').then(this.setupGuestList);
  }

  // Re-fetch & load collections without clearing
  reloadCollections() {
    return GdsApi.get('/v1/member-weddings').then(this.setupGuestList);
  }

  clearStore = () => {
    this.invitations.clear();
    this.guests.clear();
    this.gdsEvents.clear();
    this.households.clear();
  };

  setupGuestList = response => {
    runInAction(() =>
      response.households.forEach(household => {
        if (household.people && household.people.length) {
          this.households.add(new GdsHouseholdModel(household));
        }
      })
    );

    runInAction(() => response.events.forEach(event => this.gdsEvents.add(new GdsEventModel(event))));

    runInAction(() => this.guestWedding.setProps(response.guestWedding));

    runInAction(() => {
      response.households.forEach(household => {
        if (household.people) {
          household.people.forEach(person => this.guests.add(new GdsGuestModel(person, household.id)));
        }
      });
    });
  };

  updateHousehold() {
    GdsApi.get('/v1/member-weddings').then(res => {
      // Only clear out household related information
      this.invitations.clear();
      this.households.clear();
      this.guests.clear();

      this.setupGuestList(res);
    });
  }

  addGdsHousehold(householdJson) {
    const newHousehold = new GdsHouseholdModel(householdJson);

    this.households.add(newHousehold);
    newHousehold.getMembers.forEach(member => this.addGdsGuest(member));
  }

  addGdsGuest(guest) {
    this.guests.add(guest);
  }

  updatePeopleHouseholdId(people) {
    people.forEach(person => {
      const guest = this.guests.get(person.id);
      guest.householdId = person.householdId;
    });
  }

  @computed
  get householdsContactable() {
    return this.households.filter(household => household.contactable);
  }

  @computed
  get contactableHouseholdCount() {
    return this.householdsContactable.length;
  }

  @computed
  get contactableHouseholdCountEmail() {
    return this.households.filter(household => household.contactableByEmail).length;
  }

  @computed
  get contactableHouseholdCountPhone() {
    return this.households.filter(household => household.contactableByPhone).length;
  }

  @computed
  get rsvpOnCount() {
    // filter out Old Template Reception that should not be counted
    const events = this.gdsEvents.filter(event => event.rsvp && event.type !== 'Reception');
    return events.length;
  }

  @computed
  get totalHouseholdCount() {
    return this.households.size;
  }

  @computed
  get totalGuestCount() {
    return this.guests.size;
  }

  @computed
  get totalEventCount() {
    return this.gdsEvents.size;
  }

  @computed
  get rsvpPreference() {
    return this.guestWedding.isPrivateRsvp ? 'secure' : 'public';
  }

  @computed
  get overviewEventCount() {
    let hasLegacyEvents = false;
    this.gdsEvents.forEach(e => {
      if (e.type === 'Ceremony') {
        hasLegacyEvents = true;
      }
    });

    if (hasLegacyEvents) {
      return this.gdsEvents.size - 1;
    }
    return this.gdsEvents.size;
  }

  @computed
  get rsvpQuestionCount() {
    const eventQuestions = this.gdsEvents.eventQuestions || [];
    const eventLength = eventQuestions.length || 0;
    const generalQuestions = this.guestWedding.questions || [];
    const generalLength = generalQuestions.length || 0;
    return eventLength + generalLength;
  }

  @computed
  get rsvpQuestionCategories() {
    const eventQuestions = this.gdsEvents.eventQuestions || [];
    const eventTypes = eventQuestions.map(event => event.type);
    const generalQuestions = this.guestWedding.questions || [];
    const generalTypes = generalQuestions.map(event => event.type);
    const merged = [...eventTypes, ...generalTypes];
    const mergedUnique = merged.filter((v, i) => merged.indexOf(v) === i);
    return mergedUnique;
  }

  @computed
  get rsvpQuestionTypes() {
    const answerTypeToAnalyticType = {
      Option: 'choice',
      Text: 'open',
    };
    const eventQuestions = this.gdsEvents.eventQuestions || [];
    const eventAnswerTypes = eventQuestions.map(event => `event ${answerTypeToAnalyticType[event.answerType]}`);
    const generalQuestions = this.guestWedding.questions || [];
    const generalAnswerTypes = generalQuestions.map(event => `general ${answerTypeToAnalyticType[event.answerType]}`);
    const merged = [...eventAnswerTypes, ...generalAnswerTypes];
    const mergedUnique = merged.filter((v, i) => merged.indexOf(v) === i);
    return mergedUnique;
  }
}
